import { useState, useMemo, useCallback } from "react";
import React from 'react';
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportReportAsExcel, exportReportAsCsvWithPlantHeader, DEFAULT_NUMERIC_FIELDS } from '../../utils/reportExcelExport';
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import {Row,Col,Button,} from "reactstrap";
import {getDcForTally} from "../../services/ReportServices";
import {  DatePicker } from 'antd';
import { getUserAllowedPlants} from '../../services/PlantServices';
import moment  from 'moment';
import {displayError} from '../../helpers';
import { GradientCard } from '../../components/ListPageGradientSummary';
import { buildGradientCard, formatAmount, formatQty, sumField, isInactiveRecord } from '../../utils/listSummaryUtils';
import { getHTMLForTablePopup } from '../../utils/PopupUtils';

const {RangePicker} = DatePicker;

function taxColumnKey(taxName) {
    return String(taxName || '').replace(/[@.]/g, '_');
}

function formatTaxHeader(taxName) {
    const text = String(taxName || '');
    const match = text.match(/^(CGST|SGST|IGST)\s*@\s*([0-9.]+)\s*%?$/i);
    if (!match) {
        return text;
    }
    const rate = Number(match[2]);
    const rateText = Number.isFinite(rate) ? rate.toFixed(1) : match[2];
    return `${match[1].toUpperCase()} @${rateText}`;
}

function isZeroValue(value) {
    if (value === null || value === undefined || value === '') {
        return true;
    }
    const text = String(value).replace(/,/g, '').trim();
    if (!text || text === '-' || /^[-+]?0+(?:\.0+)?$/.test(text)) {
        return true;
    }
    const amount = Number(text);
    return !Number.isFinite(amount) || Number(amount.toFixed(2)) === 0;
}

function taxColumnHasData(rows, fieldName) {
    return (rows || []).some((row) => !isZeroValue(row?.[fieldName]));
}

function visibleTaxes(taxes, rows) {
    return (taxes || []).filter((tax) => taxColumnHasData(rows, taxColumnKey(tax)));
}

function formatDocNo(prefix, number) {
    const value = String(number ?? '').trim();
    const prefixText = String(prefix || '').trim();
    if (!prefixText || value.startsWith(prefixText)) {
        return value;
    }
    return `${prefixText}${value}`;
}

const TALLY_NUMERIC_FIELDS = new Set([
    ...DEFAULT_NUMERIC_FIELDS,
    'basic_rate',
    'gross_total',
    'rounded_off',
]);
function DcForTally() 
{
    const InitialData = {
        records: []
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false); 
    const [plants, setPlants] = React.useState([]);
    const [input, setInput] = useState({});
    const [taxlist, setTaxlist] = React.useState([]);

    // Initial state for from_date and to_date
    const initialFromDate = moment().startOf('month');
    const initialToDate =  moment();
    const [fromDate, setFromDate] = useState(initialFromDate);
    const [toDate, setToDate] = useState(initialToDate);
    const handleDateChange = (values) => {
        if ((values) && (values.length === 2)) {
        setFromDate(values[0]);
        setToDate(values[1]);
        }
    };

    React.useEffect(() => {
        //Load data for Plant dropdown
        getUserAllowedPlants(cookies)
        .then (
            plantList => {
                const plants = plantList.plant_list.map(
                    plant => {
                        return { value: plant.id, label: plant.plant_alias }
                    }
                );
                setPlants(plants);
            }
        )
    }, []);

    const Home = () => {
        navigate('/Home')
    }

    const columns = useMemo(
        () => {
            const baseColumns =[
            {
                fieldName: "delivery_challan_date",
                headerName: 'Date'
            },
            {
                fieldName: 'delivery_challan_no', 
                headerName: 'Voucher No.'
            },
            {
                fieldName: 'voucher_type', 
                headerName: 'Voucher Type'
            },
            {
                fieldName: 'customer_name', 
                headerName: 'Buyer (Bill to)'
            },
            {
                fieldName: 'customer_address_1', 
                headerName: 'Buyer (Bill to) Address 1'
            },
            {
                fieldName: 'customer_address_2', 
                headerName: 'Buyer (Bill to) Address 2'
            },
            {
                fieldName: 'customer_address_3', 
                headerName: 'Buyer (Bill to) Address 3'
            },
            {
                fieldName: 'customer_pincode', 
                headerName: 'Buyer (Bill to) Pincode'
            },
            {
                fieldName: 'consignee_name', 
                headerName: 'Consignee (Ship to)'
            },
            {
                fieldName: 'consignee_address_1', 
                headerName: 'Consignee (Ship to) Address 1'
            },
            {
                fieldName: 'consignee_address_2', 
                headerName: 'Consignee (Ship to) Address 2'
            },
            {
                fieldName: 'consignee_address_3', 
                headerName: 'Consignee (Ship to) Address 3'
            },
            {
                fieldName: 'consignee_pincode', 
                headerName: 'Consignee (Ship to) Pincode'
            },
            {
                fieldName: "purchase_order_no",
                headerName: 'Voucher Ref. No.'
            },
            {
                fieldName: "customer_gst_no",
                headerName: 'GSTIN/UIN'
            },
            {
                fieldName: "customer_pan_no",
                headerName: 'PAN No.'
            },
            {
                fieldName: "user_remarks",
                headerName: 'Narration'
            },
            {
                fieldName: "sales_order_no",
                headerName: 'Order No'
            },
            {
                fieldName: "sales_order_date",
                headerName: 'Order Date'
            },
            {
                fieldName: 'pay_terms', 
                headerName: 'Terms of Payment'
            },
            {
                fieldName: 'vehicle_no', 
                headerName: 'Vehicle No'
            },
            {
                fieldName: 'delivery_mode', 
                headerName: 'Terms of Delivery'
            },
            {
                fieldName: 'dispatch_through', 
                headerName: 'Despatch Through'
            },
            {
                fieldName: "project_name",
                headerName: 'Destination'
            },
            {
                fieldName: 'dc_grade', 
                headerName: 'Description of Goods'
            },
            {
                fieldName: 'dc_qty', 
                headerName: 'Quantity',
                align: 'right',
            },
            {
                fieldName: 'basic_rate', 
                headerName: 'Rate',
                align: 'right',
            },
            {
                fieldName: 'amount', 
                headerName: 'Value',
                align: 'right',
            },
            {
                fieldName: 'gross_total', 
                headerName: 'Gross Total',
                align: 'right',
            },
        ];
        const dynamicColumns = visibleTaxes(taxlist, data.records).map((tax) => ({
            fieldName: taxColumnKey(tax),
            headerName: formatTaxHeader(tax),
            align: 'right',
        }));
        const trailingColumns = [
            {
                fieldName: 'rounded_off', 
                headerName: 'Rounded Off',
                align: 'right',
            },
            {
                fieldName: 'salesrep_name',
                headerName: 'Marketting',
            },
        ];
        return [...baseColumns, ...dynamicColumns, ...trailingColumns];
    },[taxlist, data.records]);

    const reportColumns = useMemo(
        () => columns.filter((col) => (
            !taxlist.some((tax) => taxColumnKey(tax) === col.fieldName)
            || taxColumnHasData(data.records, col.fieldName)
        )),
        [columns, taxlist, data.records],
    );

    const summaryCards = useMemo(() => {
        const records = data.records || [];
        const activeRecords = records.filter((row) => !isInactiveRecord(row));
        const cancelledRecords = records.filter((row) => isInactiveRecord(row));
        return [
            buildGradientCard('VOUCHERS', activeRecords.length, 'blue'),
            buildGradientCard('TOTAL QTY', formatQty(sumField(activeRecords, 'dc_qty')), 'teal'),
            buildGradientCard('Taxable Amount', formatAmount(sumField(activeRecords, 'amount')), 'purple'),
            buildGradientCard('Invoice Amount', formatAmount(sumField(activeRecords, 'gross_total')), 'today'),
            {
                ...buildGradientCard('Cancelled', formatQty(sumField(cancelledRecords, 'dc_qty')), 'red'),
                subValue: `${cancelledRecords.length} DC cancelled`,
            },
        ];
    }, [data.records]);

    const showCancelledDcDetails = useCallback(() => {
        const cancelledRows = (data.records || []).filter((row) => isInactiveRecord(row));
        if (!cancelledRows.length) {
            Swal.fire('Cancelled DCs', 'No cancelled delivery challans in the current report.', 'info');
            return;
        }
        const totalQty = cancelledRows.reduce((sum, row) => sum + (Number(row.dc_qty) || 0), 0);
        const html = getHTMLForTablePopup(
            `Cancelled Delivery Challans (${cancelledRows.length})`,
            [
                { key: 'delivery_challan_no', label: 'Voucher No.', align: 'center' },
                { key: 'delivery_challan_date', label: 'Date', align: 'center' },
                { key: 'customer_name', label: 'Buyer', align: 'left' },
                { key: 'project_name', label: 'Site', align: 'left' },
                { key: 'dc_grade', label: 'Grade', align: 'left' },
                { key: 'vehicle_no', label: 'Vehicle', align: 'left' },
                { key: 'dc_qty', label: 'Qty', align: 'right' },
                { key: 'amount', label: 'Value', align: 'right' },
            ],
            cancelledRows.map((row) => ({
                delivery_challan_no: row.delivery_challan_no,
                delivery_challan_date: row.delivery_challan_date,
                customer_name: row.customer_name,
                project_name: row.project_name,
                dc_grade: row.dc_grade,
                vehicle_no: row.vehicle_no,
                dc_qty: formatQty(row.dc_qty),
                amount: formatAmount(row.amount),
            })),
            {
                delivery_challan_no: 'Total',
                delivery_challan_date: '',
                customer_name: '',
                project_name: '',
                dc_grade: '',
                vehicle_no: `${cancelledRows.length} DC`,
                dc_qty: formatQty(totalQty),
                amount: formatAmount(sumField(cancelledRows, 'amount')),
            },
        );
        Swal.fire({
            html,
            width: '980px',
            confirmButtonText: 'Close',
        });
    }, [data.records]);
   
    const exportFileBase = 'DC_for_TALLY';

    const handleCSVExport = async () => {
        if (!data.records.length) {
            Swal.fire('Generate the report first, then export.', '', 'info');
            return;
        }
        await exportReportAsCsvWithPlantHeader({
            columns: reportColumns,
            records: data.records,
            filename: exportFileBase,
            numericFields: TALLY_NUMERIC_FIELDS,
            includeLetterhead: false,
            blankFirstRow: true,
        });
    };

    const handleExcelExport = async () => {
        if (!data.records.length) {
            Swal.fire('Generate the report first, then export.', '', 'info');
            return;
        }
        await exportReportAsExcel({
            columns: reportColumns,
            records: data.records,
            filename: exportFileBase,
            numericFields: TALLY_NUMERIC_FIELDS,
            sheetName: 'DC for TALLY',
            blankFirstRow: true,
        });
    };
    
    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setInput(values => ({...values, [name]: event.target.value}))
    }

    const handleSubmit=(event)=> 
    {
        event.preventDefault();
        const inputs = {
            plant_id:input.plant_id,
            from_date:fromDate.format('DD-MM-YYYY'),
            upto_date:toDate.format('DD-MM-YYYY')
          }
        if ((inputs.plant_id) &&
        (inputs.from_date) && (inputs.upto_date))
        {
            setIsLoading(true);
            getDcForTally(cookies, inputs)
            .then(response => {
                const sourceRows = response.delivery_challan_list || [];
                const requestedTaxes = response.tax_list || [];
                const tableData = sourceRows.map((dc) => {
                    const taxValues = requestedTaxes.reduce((acc, tax) => {
                        const amount = Number(dc[tax] || 0);
                        acc[taxColumnKey(tax)] = isZeroValue(amount) ? '0.00' : amount.toFixed(2);
                        return acc;
                    }, {});
                    return {
                    id: dc.id,
                    dispatch_through:'Transit Mixer',
                    delivery_challan_date: dc.delivery_challan_date,
                    delivery_challan_no: formatDocNo(dc.delivery_challan_prefix, dc.delivery_challan_no),
                    status: (dc.status === false || String(dc.status || '').trim().toLowerCase() === 'cancelled')
                        ? 'Cancelled'
                        : 'Active',
                    customer_name:dc.customer_name,
                    customer_address_1:dc.customer_address_1,
                    customer_address_2:dc.customer_address_2,
                    customer_address_3:dc.customer_address_3,
                    customer_pincode:dc.customer_pincode,
                    consignee_name:dc.consignee_name,
                    consignee_address_1:dc.consignee_address_1,
                    consignee_address_2:dc.consignee_address_2,
                    consignee_address_3:dc.consignee_address_3,
                    consignee_pincode:dc.consignee_pincode,
                    purchase_order_no:dc.purchase_order_no,
                    customer_gst_no:dc.customer_gst_no,
                    customer_pan_no:dc.customer_pan_no,
                    user_remarks:dc.user_remarks,
                    sales_order_no: formatDocNo(dc.sales_order_prefix, dc.sales_order_no),
                    sales_order_date: dc.sales_order_date,
                    pay_terms:dc.pay_terms,
                    vehicle_no: dc.vehicle_no,
                    delivery_mode: dc.delivery_mode,
                    project_name:dc.project_name,
                    dc_grade:  dc.dc_grade,
                    dc_qty: Number(dc.dc_qty || 0).toFixed(2),
                    basic_rate: Number(dc.basic_rate || 0).toFixed(2),
                    amount:  Number(dc.amount || 0).toFixed(2),
                    gross_total: Number(dc.gross_total || 0).toFixed(2),
                    rounded_off: Number(dc.rounded_off || 0).toFixed(2),
                    salesrep_name: dc.salesrep_name,
                    voucher_type:'Delivery Note',
                    ...taxValues,
                    };
                });
                const taxes = visibleTaxes(requestedTaxes, tableData);
                setTaxlist(taxes);
                setData({
                    records: tableData
                });
            }).catch((error) => {
                displayError(error.response?.data || error);
            }).finally(() => {
                setIsLoading(false);
            });
        }
        else 
        {
            Swal.fire("plant,from_date,upto_date must not be empty.", "", "info");  
        }
    }

    return (
        <div className="report-embedded-page">
            <form className="form" onSubmit={handleSubmit}>
                <div className="container">
                    <Row>
                        <Col xl={12} lg={12} md={12}>
                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'nowrap', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                <select id="plant_id" name="plant_id"  value={input.plant_id || ""} onChange={handleChange} className="form-control browser-default custom-select mandatory-form-control" required style={{ width: '240px' }} >
                                <option value="">Select plant</option>
                                
                                    {plants.map((item) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}</option>
                                    ))}
                                </select>&nbsp;&nbsp;
                                    <RangePicker
                                        defaultValue={[initialFromDate, initialToDate]}
                                        format="DD-MM-YYYY"
                                        onChange={handleDateChange}
                                        style={{height:"40px",width:"265px"}}
                                    />&nbsp;&nbsp;
                                        
                                    <Button  type="submit" style={{backgroundColor: "RGB(188,232,253)",fontWeight:"bold",height:"30px",display: "flex",
                                        alignItems: "center",  
                                        justifyContent: "center"}} >
                                        <i className="fa fa-search fa-2x" id="myData" aria-hidden="true" style={{color:"rgb(93, 156, 233)"}}></i>
                                    </Button>
                                </div>
                                {data.records.length > 0 ? (
                                    <div style={{ display: 'flex', alignItems: 'stretch', gap: '8px', minWidth: 0, overflowX: 'auto', marginLeft: 'auto' }}>
                                        {summaryCards.map((card) => (
                                            <GradientCard
                                                key={card.label}
                                                card={card}
                                                compact
                                                onClick={card.label === 'Cancelled' ? showCancelledDcDetails : undefined}
                                            />
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        </Col>
                    </Row> 
                </div>
            </form>
            <div className="container">
                <div className="list-page-table-wrap">
                    <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title='DC for TALLY'
                                columns={reportColumns}
                                data={data.records}
                                onExcelExport={handleExcelExport}
                                onCSVExport={handleCSVExport}
                                isLoading={isLoading}
                                wrapText
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
);
}
export default DcForTally;
